import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';

import {
    CalculatorStepPanel,
    getEmptyNumberPlaceholder,
    Sizes,
    Question,
    QuestionMultiInput,
    FormComputed,
    MessageBanner,
    MessagePropType,
    MessageBannerVariants,
    MessageBannerCTAVariants,
} from '@ratehub/base-ui';
import { MORTGAGE_PLACEHOLDERS } from '@ratehub/mtg-common';

import COMPOUND_FREQUENCY_OPTIONS from '../definitions/CompoundFrequencyOptions';
import calculateTotalInterestEarned from '../functions/calculateTotalInterestEarned';
import calculateTotalInvestmentValue from '../functions/calculateTotalInvestmentValue';


function CompoundInterestCalcInputStep({ messageBannerProps }) {
    const intl = useIntl();

    const shouldShowMessageBanner = !!(messageBannerProps?.title || messageBannerProps?.message);

    return (
        <Container
            className="rh-pt-1_5 rh-pb-0 rh-px-1_5"
            stepNumber={1}
            isVisible
        >

            <FormComputed
                name="calculatedTotalInvestmentValue"
                value={calculateTotalInvestmentValue}
            />

            <h2 className="rh-text-l rh-mt-0 rh-mb-1_5 weight-regular">
                <FormattedMessage
                    id="calculators.Compound-Interest.header"
                    defaultMessage="Calculate your compound interest"
                />
            </h2>

            <hr className="rh-horizontal-rule rh-mb-1_5 rh-width-100p" />

            <Question
                className="question-container"
                field={{
                    name: 'initialInvestment',
                    type: 'integer',
                    required: true,
                    validators: [
                        {
                            fn: isAmountValid,
                            message: (
                                <FormattedMessage
                                    id="calculators.Compound-Interest.initialInvestmentLessThanOneErrorMessage"
                                    defaultMessage="Amount must be at least $1"
                                />),
                        },
                    ],
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.initialInvestmentLabel"
                        defaultMessage="Initial investment"
                    />
                )}
                placeholder={MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.initialInvestmentQuicktip"
                        defaultMessage="The initial amount of money you’re depositing upfront."
                    />
                )}
                type="currency"
                isSideBySide={true}
            />

            <QuestionMultiInput
                fields={ADDITIONAL_CONTRIBUTIONS_FIELDS}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.additionalContributionsLabel"
                        defaultMessage="Additional contributions"
                    />
                )}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.additionalContributionsQuicktip"
                        defaultMessage="Include how much (and how often) you’ll regularly add to your initial investment."
                    />
                )}
                isOptional={true}
                isSideBySide={true}
                isInline={true}
                hideLabels={true}
                errorMessageStyle="minimal"
            />

            <Question
                className="question-container"
                field={{
                    name: 'compoundInterestRate',
                    type: 'float',
                    required: true,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.compoundInterestRateLabel"
                        defaultMessage="Interest Rate"
                    />
                )}
                placeholder={MORTGAGE_PLACEHOLDERS.PERCENT_PLACEHOLDER}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.compoundInterestRateQuicktip"
                        defaultMessage="The net gain of your investment."
                    />
                )}
                type="percent"
                isSideBySide={true}
            />

            <Question
                className="question-container"
                field={{
                    name: 'compoundFrequency',
                    type: 'choice',
                    value: COMPOUND_FREQUENCY_OPTIONS.ANNUALLY,
                    options: Object.values(COMPOUND_FREQUENCY_OPTIONS),
                }}
                type="dropdown"
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.compoundFrequencyLabel"
                        defaultMessage="Compound frequency"
                    />
                )}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.compoundFrequencyQuicktip"
                        defaultMessage="Most investments compound annually."
                    />
                )}
                tooltipMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.compoundFrequencyTooltip"
                        defaultMessage="Compounding refers to earning “interest on interest” which means you’re reinvesting and earning interest on your initial investment plus on the interest you’ve accumulated. Interest can be reinvested annually, monthly, or daily."
                    />
                )}
                isSideBySide={true}
            />

            <Question
                className="question-container"
                field={{
                    name: 'timeHorizon',
                    type: 'integer',
                    required: true,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.timeHorizonLabel"
                        defaultMessage="Time horizon (in years)"
                    />
                )}
                quicktipMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.timeHorizonQuicktip"
                        defaultMessage="The number of years you plan to invest your money."
                    />
                )}
                isSideBySide={true}
            />

            {/* TODO - the following two Questions might be better expressed as <output> within <FormComputed>
                     - if making changes to this component, please investigate this as a refactor */}
            <Question
                className="question-container"
                field={{
                    name: 'totalInvestmentValue',
                    type: 'integer',
                    formula: calculateTotalInvestmentValue,
                    // required must be false, otherwise when there are cents, it displays a warning message about an invalid value
                    required: false,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.totalInvestmentValue"
                        defaultMessage="Total value of investment"
                    />
                )}
                placeholder={getEmptyNumberPlaceholder('CAD0', intl.locale)}
                tooltipMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.totalInvestmentValueTooltip"
                        defaultMessage="Your total returns including your initial investment, additional contributions, and interest you’ve earned."
                    />
                )}
                tooltipLocation="top"
                tooltipAlignment="end"

                type="currency"
                isSideBySide={true}
                showOptionalText={false}
                readOnly={true}
                inputClassName="calculation-output rh-title-xs leading-xs rh-mt-0 rh-mb-2"
            />

            <Question
                className="question-container last-question"
                field={{
                    name: 'totalInterestEarned',
                    type: 'integer',
                    formula: calculateTotalInterestEarned,
                    // required must be false, otherwise when there are cents, it displays a warning message about an invalid value
                    required: false,
                }}
                labelMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.totalInterestEarned"
                        defaultMessage="Total interest earned"
                    />
                )}
                placeholder={getEmptyNumberPlaceholder('CAD0', intl.locale)}
                tooltipMessage={(
                    <FormattedMessage
                        id="calculators.Compound-Interest.totalInterestEarnedTooltip"
                        defaultMessage="The amount of interest income you’ve earned (not including your initial investment and contributions)."
                    />
                )}
                tooltipLocation="top"
                tooltipAlignment="end"

                type="currency"
                isSideBySide={true}
                showOptionalText={false}
                readOnly={true}
                inputClassName="calculation-output rh-title-xs leading-xs rh-mt-0 rh-mb-2"
            />

            <If condition={shouldShowMessageBanner}>
                <MessageBanner
                    className="rh-mb-2"
                    variant={MessageBannerVariants.NOTIFICATION}

                    {...messageBannerProps}
                    messageSize="small"

                    ctaDataName="compound-interest-learnMore"
                    ctaVariant={MessageBannerCTAVariants.PRIMARY_ANCHOR}
                />
            </If>
        </Container>
    );
}

CompoundInterestCalcInputStep.propTypes = {
    messageBannerProps: PropTypes.shape({
        title: MessagePropType,
        message: MessagePropType,
        href: PropTypes.string,
        ctaMessage: MessagePropType,
    }),
};


const ADDITIONAL_CONTRIBUTIONS_FIELDS = [
    {
        field: {
            name: 'additionalContributions',
            type: 'integer',
            required: false,
        },
        label: MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER, // acts as placeholder
        type: 'currency',
    },
    {
        field: {
            name: 'additionalContributionsFrequency',
            type: 'choice',
            required: true,
            value: COMPOUND_FREQUENCY_OPTIONS.ANNUALLY,
            options: Object.values(COMPOUND_FREQUENCY_OPTIONS),
        },
        label: 'label', // added because label is a required field; has no effect visually
        type: 'dropdown',
    },
];

// validates if user inputted an amount greater than 1
function isAmountValid(amount) {
    return amount >= 1;
}


const Container = styled(CalculatorStepPanel)`
    // override question vertical spacing
    .question-container {
        margin-top: 0;
        margin-bottom: ${Sizes.SPACING.TWO};
    }

    .calculation-output {
        height: ${Sizes.SPACING.TWO};
    }

    .last-question {
        margin-top: -${Sizes.SPACING.TWO};
    }
`;

export default observer(CompoundInterestCalcInputStep);
